// @flow

import React, { type Node, useState, useCallback, useRef, useEffect } from 'react';
import { useTenant } from '@tectonics/commons/tenant';
import { usePayment } from '../context';
import { completeHostedPayment, COMPLETE_ERROR_MESSAGE } from '../services/payment-api';
import {
  maskPhone,
  unmaskPhone,
  isValidPhone,
  countDigits,
  caretPositionAfterDigits,
  PHONE_SEPARATOR,
} from '../utils/phone-mask';
import { resolveWaitingDeadline } from '../hooks/waiting-deadline';
import yapeAvatarSrc from '../yape-avatar.svg';
import {
  Container,
  Content,
  Title,
  Subtitle,
  Form,
  Avatar,
  Label,
  InputWrapper,
  CountryPrefix,
  PhoneInput,
  VisuallyHidden,
  ErrorMessage,
  InfoBox,
  InfoIcon,
  InfoText,
  InfoLink,
  SubmitButton,
} from './phone-input-screen.styles';

const VALIDATION_ERROR = 'Verifica tu número de celular e inténtalo de nuevo.';
const PHONE_INPUT_ID = 'yape-phone-input';
const PHONE_HINT_ID = 'yape-phone-input-hint';
const PHONE_HINT = 'Número de celular peruano de 9 dígitos con el código de país +51.';

export function PhoneInputScreen(): Node {
  const tenant = useTenant();
  const { data, dispatch } = usePayment();
  const [phone, setPhone] = useState('');
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<?HTMLInputElement>(null);
  const caretRef = useRef<?number>(null);
  const isSubmitting = data.state === 'SUBMITTING';
  const isPhoneValid = isValidPhone(phone);
  const hasPhoneError = touched && phone !== '' && !isPhoneValid;

  useEffect(() => {
    const input = inputRef.current;
    const caret = caretRef.current;
    caretRef.current = null;

    if (input && caret != null && document.activeElement === input) {
      input.setSelectionRange(caret, caret);
    }
  }, [phone]);

  const handlePhoneChange = useCallback((event: SyntheticInputEvent<HTMLInputElement>) => {
    const { value, selectionStart } = event.currentTarget;
    const caret = selectionStart === null ? value.length : selectionStart;
    const maskedValue = maskPhone(value);

    caretRef.current = caretPositionAfterDigits(maskedValue, countDigits(value.slice(0, caret)));
    setPhone(maskedValue);
  }, []);

  const handlePhoneKeyDown = useCallback((event: SyntheticKeyboardEvent<HTMLInputElement>) => {
    const { value, selectionStart, selectionEnd } = event.currentTarget;

    if (event.key !== 'Backspace' || selectionStart === null || selectionStart !== selectionEnd) {
      return;
    }

    const caret: number = selectionStart;

    if (caret === 0 || value[caret - 1] !== PHONE_SEPARATOR) {
      return;
    }

    event.preventDefault();

    const digitsBeforeSeparator = countDigits(value.slice(0, caret)) - 1;
    const maskedValue = maskPhone(value.slice(0, caret - 2) + value.slice(caret));

    caretRef.current = caretPositionAfterDigits(maskedValue, digitsBeforeSeparator);
    setPhone(maskedValue);
  }, []);

  const handlePhoneBlur = useCallback(() => {
    setTouched(true);
  }, []);

  const handleSubmit = useCallback(async (event: SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault();
    setTouched(true);

    if (!isValidPhone(phone)) {
      return;
    }

    dispatch({ type: 'START_SUBMITTING' });

    try {
      const response = await completeHostedPayment(
        data.paymentHash,
        unmaskPhone(phone),
        tenant.code,
      );

      if (response.status === 'SUCCESS') {
        dispatch({
          type: 'START_WAITING_APPROVAL',
          waitingDeadline: resolveWaitingDeadline(null),
        });
      } else {
        dispatch({
          type: 'SET_ERROR',
          errorMessage: response.status_message || COMPLETE_ERROR_MESSAGE,
          retryState: 'COLLECT_PHONE',
        });
      }
    } catch (err) {
      dispatch({
        type: 'SET_ERROR',
        errorMessage: err.message || COMPLETE_ERROR_MESSAGE,
        retryState: 'COLLECT_PHONE',
      });
    }
  }, [phone, data.paymentHash, tenant.code, dispatch]);

  return (
    <Container>
      <Content>
        <Avatar src={yapeAvatarSrc} alt="Yape" />
        <Title>Paga con Yape</Title>
        <Subtitle>
          Ingresa el número de celular vinculado a tu cuenta Yape para aprobar la compra.
        </Subtitle>

        <Form onSubmit={handleSubmit}>
          <Label htmlFor={PHONE_INPUT_ID}>Número de celular (Yape)</Label>
          <InputWrapper hasError={hasPhoneError}>
            <CountryPrefix aria-hidden="true">+51</CountryPrefix>
            <PhoneInput
              id={PHONE_INPUT_ID}
              ref={inputRef}
              type="tel"
              inputMode="numeric"
              autoComplete="tel-national"
              placeholder="900 000 000"
              aria-describedby={PHONE_HINT_ID}
              aria-invalid={hasPhoneError}
              value={phone}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyDown}
              onBlur={handlePhoneBlur}
              hasError={hasPhoneError}
            />
          </InputWrapper>
          <VisuallyHidden id={PHONE_HINT_ID}>{PHONE_HINT}</VisuallyHidden>
          {hasPhoneError ? <ErrorMessage>{VALIDATION_ERROR}</ErrorMessage> : null}

          <InfoBox>
            <InfoIcon aria-hidden="true">i</InfoIcon>
            <InfoText>
              Para recibir la notificación del pago, ingresa el número de celular
              vinculado a tu cuenta Yape. Si aún no tienes la aplicación, haz{' '}
              <InfoLink
                href="https://www.yape.com.pe/"
                target="_blank"
                rel="noopener noreferrer"
              >
                clic aquí
              </InfoLink>{' '}
              para descargarla.
            </InfoText>
          </InfoBox>

          <SubmitButton type="submit" disabled={!isPhoneValid || isSubmitting}>
            Solicitar aprobación
          </SubmitButton>
        </Form>
      </Content>
    </Container>
  );
}
